import { useRef, useState } from "react";

import { Eye, EyeOff } from "lucide-react";

import {
  ULThemeFloatingLabelField,
  type ULThemeFloatingLabelFieldProps,
} from "@/components/form/ULThemeFloatingLabelField";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface ULThemePasswordFieldProps
  extends Omit<ULThemeFloatingLabelFieldProps, "type" | "endAdornment"> {
  onVisibilityToggle?: (isVisible: boolean) => void;
  buttonClassName?: string;
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
}

export const ULThemePasswordField = ({
  onVisibilityToggle,
  buttonClassName,
  showPasswordLabel = "Show password",
  hidePasswordLabel = "Hide password",
  ...props
}: ULThemePasswordFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  // TooltipPrimitive.Portal defaults to appending to <body>, which escapes
  // the .theme-universal/.hotels-theme scoped ancestor this component relies
  // on for its accent color - mounting the portal inside this component's
  // own (in-tree) container keeps the tooltip themed correctly, matching the
  // same fix used by ULThemeCountryCodeDropdown's Combobox.Portal.
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    const newState = !showPassword;
    setShowPassword(newState);
    onVisibilityToggle?.(newState);
  };

  const passwordButton = (
    <Tooltip>
      <TooltipTrigger
        type="button"
        onClick={handleToggle}
        className={cn(
          // Layout & Positioning
          "cursor-pointer h-full w-full min-w-[44px] mr-[-5px]",

          // Border Radius - matches input field
          "theme-universal:rounded-r-input theme-universal:rounded-l-none",

          // Colors
          "theme-universal:text-input-labels",
          "theme-universal:hover:text-input-text",

          // Transitions
          "transition-colors",

          // Focus States
          "theme-universal:focus:bg-base-focus/15 theme-universal:focus-visible:ring-1 theme-universal:focus-visible:ring-base-focus theme-universal:focus-visible:ring-offset-0",

          // Layout
          "flex items-center justify-center",

          // Button-like styles
          "bg-transparent border-none outline-none",

          buttonClassName,
        )}
        aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
      >
        {showPassword ? <EyeOff /> : <Eye />}
      </TooltipTrigger>
      <TooltipContent
        sideOffset={0}
        container={containerRef}
        className="theme-universal:bg-(--ul-theme-color-accent) text-white -mb-2 ml-0.5 theme-universal:rounded-button"
      >
        {showPassword ? hidePasswordLabel : showPasswordLabel}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <div ref={containerRef}>
      <ULThemeFloatingLabelField
        {...props}
        type={showPassword ? "text" : "password"}
        endAdornment={passwordButton}
      />
    </div>
  );
};

ULThemePasswordField.displayName = "ULThemePasswordField";
